import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const BRAND = { purple: "#a78bfa", pink: "#ec4899" };

// ---------------------------------------------------------------------------
// Theme tokens — everything text/track/card related reads from here so the
// whole card can flip between dark and light without touching layout logic.
// ---------------------------------------------------------------------------
function getTheme(theme) {
  if (theme === "light") {
    return {
      label: "text-slate-800",
      labelDim: "text-slate-400",
      sub: "text-slate-500",
      track: "rgba(120,110,180,0.14)",
      cardBg: "bg-white/70",
      cardBorder: "border-black/5",
      navBorder: "border-black/10",
      navText: "text-slate-500",
      dotInactive: "rgba(0,0,0,0.12)",
      glow: "rgba(167,139,250,0.18)",
    };
  }
  return {
    label: "text-white",
    labelDim: "text-slate-400",
    sub: "text-slate-400",
    track: "rgba(148,130,255,0.12)",
    cardBg: "bg-white/[0.03]",
    cardBorder: "border-white/5",
    navBorder: "border-white/10",
    navText: "text-slate-300",
    dotInactive: "rgba(255,255,255,0.15)",
    glow: "rgba(167,139,250,0.25)",
  };
}

// ---------------------------------------------------------------------------
// Merge averages (the actual scores) with fieldsConfig (key/label/color meta)
// No generated palette, no lightening — colors come straight from config,
// exactly like the reference: colorFrom = f.colorFrom ?? f.color ?? BRAND.purple
// ---------------------------------------------------------------------------
function buildFields(
  averages,
  fieldsConfig = [],
  maxPerField = 15,
  maxOverrides = {},
) {
  const configByKey = Object.fromEntries(fieldsConfig.map((f) => [f.key, f]));

  return Object.entries(averages || {}).map(([key, value]) => {
    const cfg = configByKey[key] ?? {};
    return {
      key,
      label: cfg.label ?? key,
      value: Number(value) || 0,
      max: maxOverrides[key] ?? cfg.max ?? maxPerField,
      colorFrom: cfg.colorFrom ?? cfg.color ?? BRAND.purple,
      colorTo: cfg.colorTo ?? cfg.color ?? BRAND.pink,
      icon: cfg.icon,
    };
  });
}

// Plain pagination: chunk fields N-at-a-time per page (default 4). No more
// "lead" page / total dial — every page is just a grid of dials.
const FIELDS_PER_PAGE = 4;

function paginateFields(fields, perPage = FIELDS_PER_PAGE) {
  const pages = [];
  let rest = fields;

  while (rest.length) {
    pages.push({ kind: "grid", fields: rest.slice(0, perPage) });
    rest = rest.slice(perPage);
  }

  return pages;
}

// ---------------------------------------------------------------------------
// Dial primitives
// ---------------------------------------------------------------------------
function PremiumDial({
  id,
  pct,
  colorFrom,
  colorTo,
  size = 72,
  stroke = 6,
  active,
  icon: Icon,
  big = false,
  track,
  labelClass,
}) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (Math.min(100, Math.max(0, pct)) / 100) * c;

  return (
    <div
      className="relative transition-transform duration-300"
      style={{ width: size, height: size, transform: active ? "scale(1.06)" : "scale(1)" }}
    >
      <svg
        width={size}
        height={size}
        className="-rotate-90"
        style={{
          filter: active ? `drop-shadow(0 0 10px ${colorTo}aa)` : `drop-shadow(0 1px 2px rgba(0,0,0,0.08))`,
          transition: "filter 0.3s ease",
          overflow: "visible",
        }}
      >
        <defs>
          <linearGradient
            id={`dial-grad-${id}`}
            x1="0%"
            y1="0%"
            x2="100%"
            y2="100%"
          >
            <stop offset="0%" stopColor={colorFrom} />
            <stop offset="100%" stopColor={colorTo} />
          </linearGradient>
        </defs>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke={track}
          strokeWidth={stroke}
          fill="none"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke={`url(#dial-grad-${id})`}
          strokeWidth={stroke}
          fill="none"
          strokeDasharray={c}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{
            transition: "stroke-dashoffset 0.9s cubic-bezier(.34,1.56,.64,1)",
          }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {Icon ? (
          <Icon size={13} style={{ color: colorTo }} className="mb-0.5" />
        ) : null}
        <span
          className={(big ? "text-xl" : "text-xs") + " font-bold tabular-nums " + labelClass}
        >
          {pct}%
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Straight grid layout: a 5-item (2 / 1 / 2) layout when the group is
// exactly five, otherwise a plain 2-column grid that centers a trailing odd
// item (e.g. 4 boxes = clean 2x2, 3 boxes = 2 then 1 centered, etc).
// ---------------------------------------------------------------------------
function DimensionalResultsGrid({ fields, hovered, pinned, setHovered, setPinned, size = 80, t }) {
  const displayed = hovered ?? pinned;
  const isFive = fields.length === 5;
  const isOdd = fields.length % 2 !== 0;

  const renderDial = (f, i) => {
    const pct = f.max > 0 ? Math.round((f.value / f.max) * 100) : 0;
    const isActive = displayed === f.key;
    const isLastOdd = !isFive && isOdd && i === fields.length - 1;

    return (
      <div
        key={f.key}
        className={
          "flex flex-col items-center justify-center gap-1 rounded-2xl transition-all duration-300 cursor-pointer" +
          (isLastOdd ? " col-span-2" : "")
        }
        style={{ animation: "fadeUp .5s ease both", animationDelay: `${i * 70}ms` }}
        onMouseEnter={() => setHovered(f.key)}
        onMouseLeave={() => setHovered(null)}
        onClick={() => setPinned((p) => (p === f.key ? null : f.key))}
      >
        <PremiumDial
          id={f.key}
          pct={pct}
          colorFrom={f.colorFrom}
          colorTo={f.colorTo}
          active={isActive}
          icon={f.icon}
          size={size}
          track={t.track}
          labelClass={t.label}
        />
        <span
          className={
            "text-center text-[11px] font-semibold leading-tight transition-opacity " +
            t.label +
            (displayed && !isActive ? " opacity-50" : "")
          }
        >
          {f.label}
        </span>
        <span className={"text-[10px] tabular-nums " + t.sub}>
          {f.value}/{f.max}
        </span>
      </div>
    );
  };

  if (isFive) {
    return (
      <div className="space-y-2">
        <div className="grid grid-cols-2 justify-items-center">{fields.slice(0, 2).map((f, i) => renderDial(f, i))}</div>
        <div className="flex justify-center">{renderDial(fields[2], 2)}</div>
        <div className="grid grid-cols-2 justify-items-center">{fields.slice(3).map((f, i) => renderDial(f, i + 3))}</div>
      </div>
    );
  }

  return <div className="grid grid-cols-2 gap-x-3 gap-y-4 justify-items-center">{fields.map((f, i) => renderDial(f, i))}</div>;
}

export default function PagedResultsCard({
  testName,
  result,
  fieldsConfig,
  maxPerField = 15,
  maxOverrides,
  title = "Test Taken: Results",
  subtitle,
  dropdownLabel,
  theme = "dark", // "dark" | "light"
  fieldsPerPage = FIELDS_PER_PAGE,
}) {
  const [page, setPage] = useState(0);
  const [hovered, setHovered] = useState(null);
  const [pinned, setPinned] = useState(null);

  const t = getTheme(theme);

  const averages = result?.averages ?? {};

  const fields = buildFields(averages, fieldsConfig, maxPerField, maxOverrides);
  const pages = paginateFields(fields, fieldsPerPage);

  const goTo = (next) => {
    setPage(Math.max(0, Math.min(pages.length - 1, next)));
    setHovered(null);
    setPinned(null);
  };

  if (fields.length === 0) {
    return (
      <div
        className={
          "w-full max-w-sm rounded-3xl border p-5 text-center text-sm shadow-2xl " +
          t.cardBg + " " + t.cardBorder + " " + t.sub
        }
      >
        No results to show yet.
      </div>
    );
  }

  return (
    <div className="w-full max-w-full mx-auto rounded-3xl px-5 p2-6">
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Sliding pages */}
      <div className="overflow-hidden py-4">
        <div
          className="flex transition-transform duration-500 ease-[cubic-bezier(.65,0,.35,1)]"
          style={{ transform: `translateX(-${page * 100}%)` }}
        >
          {pages.map((p, i) => (
            <div key={i} className="w-full shrink-0">
              <div className="py-1">
                <DimensionalResultsGrid
                  fields={p.fields}
                  hovered={hovered}
                  pinned={pinned}
                  setHovered={setHovered}
                  setPinned={setPinned}
                  t={t}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer + slider nav */}
      <div className="pt-3">
        {pages.length > 1 && (
          <div className="mt-3 flex items-center justify-center gap-4">
            <button
              onClick={() => goTo(page - 1)}
              disabled={page === 0}
              className={
                "flex h-8 w-8 items-center justify-center rounded-full border transition-opacity disabled:opacity-25 " +
                t.navBorder + " " + t.navText
              }
              aria-label="Previous page"
            >
              <ChevronLeft size={16} />
            </button>

            <div className="flex items-center gap-1.5">
              {pages.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  aria-label={`Go to page ${i + 1}`}
                  className="h-1.5 rounded-full transition-all duration-300"
                  style={{
                    width: page === i ? 18 : 6,
                    backgroundColor: page === i ? BRAND.pink : t.dotInactive,
                  }}
                />
              ))}
            </div>

            <button
              onClick={() => goTo(page + 1)}
              disabled={page === pages.length - 1}
              className={
                "flex h-8 w-8 items-center justify-center rounded-full border transition-opacity disabled:opacity-25 " +
                t.navBorder + " " + t.navText
              }
              aria-label="Next page"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}